import { Box, CircularProgress, Divider, Grid, Stack, Typography, styled } from "@mui/material";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MyTrainingsViewContext } from "context/myTrainings/MyTrainingsViewContext";
import { RewardType } from "models/RewardType";

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    marginBottom: theme.spacing(1),
}));

const LoadingBox = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
}));

const ValueTypography = styled(Typography)({
    textAlign: "right",
});

const IndentedGrid = styled(Grid)({
    paddingLeft: "10px",
    "&&": {
        marginTop: "-5px",
    },
});

export const MobileHomeStatisticsComponent = () => {
    const { t } = useTranslation();
    const { trainingStatistics, getStatisticsPending, isStatisticOfLoggedUser } =
        useContext(MyTrainingsViewContext);

    const rewardCounts = useMemo(() => {
        const counts: Record<RewardType, number> = {
            [RewardType.Gold]: 0,
            [RewardType.Silver]: 0,
            [RewardType.Bronze]: 0,
            [RewardType.None]: 0,
        };
        if (trainingStatistics) {
            for (const progress of trainingStatistics.actualProgresses) {
                if (progress.rewardType in counts) {
                    counts[progress.rewardType] += 1;
                }
            }
        }
        return counts;
    }, [trainingStatistics]);

    if (getStatisticsPending) {
        return (
            <LoadingBox>
                <CircularProgress />
            </LoadingBox>
        );
    }

    if (!trainingStatistics){
        return null;
    }

    return (
        <>
            <Stack spacing={1}>
                <SectionTitle variant="subtitle1">
                    {isStatisticOfLoggedUser
                        ? t("myTrainings.yourStatistics")
                        : t("myTrainings.userStatistics")}
                </SectionTitle>
                <IndentedGrid container spacing={0.5}>
                    <Grid item xs={9}>
                        <Typography variant="body2">{t("myTrainings.numberOfTrainingsAssaignedToUser")}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <ValueTypography variant="body2">{trainingStatistics.lessonsCount}</ValueTypography>
                    </Grid>
                    <Grid item xs={9}>
                        <Typography variant="body2">{t("myTrainings.allLessonsProgress")}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <ValueTypography variant="body2">{trainingStatistics.allLessonsProgress + "%"}</ValueTypography>
                    </Grid>
                    <Grid item xs={9}>
                        <Typography variant="body2">{t("myTrainings.sumOfPointsScored")}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <ValueTypography variant="body2">
                            {trainingStatistics.sumOfPointsScored + " / " + trainingStatistics.sumOfMaxPoints}
                        </ValueTypography>
                    </Grid>
                    <Grid item xs={9}>
                        <Typography variant="body2">{t("myTrainings.placeInOrganizationRanking")}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <ValueTypography variant="body2">
                            {trainingStatistics.placeInOrganizationRanking + " / " + trainingStatistics.organizationParticipantsCount}
                        </ValueTypography>
                    </Grid>
                </IndentedGrid>
            </Stack>

            <Divider />

            <Stack spacing={1}>
                <SectionTitle variant="subtitle1">
                    {isStatisticOfLoggedUser
                        ? t("myTrainings.yourRewards")
                        : t("myTrainings.userRewards")}
                </SectionTitle>
                <IndentedGrid container spacing={0.5}>
                    <Grid item xs={9}>
                        <Typography variant="body2">{t("rewardType.gold")}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <ValueTypography variant="body2">{rewardCounts[RewardType.Gold]}</ValueTypography>
                    </Grid>
                    <Grid item xs={9}>
                        <Typography variant="body2">{t("rewardType.silver")}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <ValueTypography variant="body2">{rewardCounts[RewardType.Silver]}</ValueTypography>
                    </Grid>
                    <Grid item xs={9}>
                        <Typography variant="body2">{t("rewardType.bronze")}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <ValueTypography variant="body2">{rewardCounts[RewardType.Bronze]}</ValueTypography>
                    </Grid>
                    <Grid item xs={9}>
                        <Typography variant="body2">{t("rewardType.none")}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <ValueTypography variant="body2">{rewardCounts[RewardType.None]}</ValueTypography>
                    </Grid>
                </IndentedGrid>
            </Stack>
        </>
    );
};
